import { useState, useCallback, useEffect } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { ServiceRate } from '../../config';

export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  type: string;
}

export interface ChargeRate {
  employee_id: string;
  charge_rate: number;
}

export interface UseStaticDataResult {
  serviceRates: ServiceRate[];
  teamMembers: TeamMember[];
  chargeRates: ChargeRate[];
  getChargeRate: (teamMemberId: string) => number | null;
  refetch: () => Promise<void>;
}

/**
 * Loads global lookup data used by the invoice editor sidebar (service rates,
 * active team members, current charge rates). All three are project-agnostic
 * and refresh together.
 */
export function useStaticData(): UseStaticDataResult {
  const supabase = useSupabaseClient();
  const session = useSession();

  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [chargeRates, setChargeRates] = useState<ChargeRate[]>([]);

  const refetch = useCallback(async () => {
    try {
      const [ratesRes, teamRes, chargeRes] = await Promise.all([
        supabase.from('service_rates').select('*').eq('is_active', true).order('job_type').order('rate_code'),
        supabase.from('active_team_members').select('id, first_name, last_name, type').order('first_name'),
        supabase.from('charge_rates').select('employee_id, charge_rate').is('end_date', null),
      ]);

      if (ratesRes.data) setServiceRates(ratesRes.data);
      if (teamRes.data) setTeamMembers(teamRes.data);
      if (chargeRes.data) setChargeRates(chargeRes.data);
    } catch (err) {
      console.error('Error fetching static data:', err);
    }
  }, [supabase]);

  useEffect(() => {
    if (session?.user) {
      void refetch();
    }
  }, [session, refetch]);

  const getChargeRate = useCallback((teamMemberId: string): number | null => {
    const rate = chargeRates.find(r => r.employee_id === teamMemberId);
    return rate ? rate.charge_rate : null;
  }, [chargeRates]);

  return { serviceRates, teamMembers, chargeRates, getChargeRate, refetch };
}
